import {
  Box,
  FormControl,
  OutlinedInput,
  TextField,
  MenuItem,
  Checkbox,
  Select,
  ListItemText,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import NumberFormat from 'react-number-format';
import { getPlacesAutocomplete, getTypeProperties } from '../../api';
import { getSearchObjectFromAutocomplete } from '../../helpers/general';
import useOptionsData from '../../hooks/useOptionsData';
import AutoComplete from '../AutoComplete';
import Button from '../core/Button';
import styles from './Filters.module.scss';
import Modal from './Modal';
import AlertsIcon from '../../fonts/icons/alerts.svg';
import SaveFilterDialog from '../SaveFilterDialog';
interface Props {
  open: boolean;
  initialFilterStr?: string;
  onCleanFilters?: () => void;
  onSubmit: (filterJson: Record<any, any>) => void;
  onClose: () => void;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function setMinMaxFilter(key: string, minValue: any, maxValue: any) {
  if (!minValue && !maxValue) return { [key]: undefined };

  return {
    [key]: {
      ...(minValue && { $gte: minValue }),
      ...(maxValue && { $lte: maxValue }),
    },
  };
}

function getOrFilters(key: string, data: any[]) {
  return data.map((el) => {
    const valGte = el.split('+');

    return {
      [key]: { [valGte.length > 1 ? '$gte' : '$eq']: parseInt(valGte[0]) },
    };
  });
}

function parseOrFilters(key: string, data: any[]) {
  if (!data) return;

  const orEl = data.find((el) => Boolean(el.$or?.[0][key]))?.$or;
  if (!orEl) return;

  const parsedData = orEl
    .map((el: any) =>
      el[key]?.$gte ? el[key].$gte.toString() + '+' : el[key]?.$eq.toString()
    )
    .filter((el: any) => Boolean(el));

  return parsedData.length > 0 ? parsedData : undefined;
}
//Interfaces
interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}
//Custom component currency
const NumberFormatCustom = React.forwardRef<NumberFormat, CustomProps>(
  function NumberFormatCustom(props, ref) {
    const { onChange, ...other } = props;

    return (
      <NumberFormat
        {...other}
        ref={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.value,
            },
          });
        }}
        thousandSeparator
        isNumericString
        prefix="$"
      />
    );
  }
);

export default function Filters(props: Props) {
  if (!props.open) return null;

  const defaultValues = useMemo(() => {
    const initialFilters =
      props.initialFilterStr && JSON.parse(props.initialFilterStr);

    const searchStrKey = Boolean(initialFilters?.['address.country'])
      ? 'address.country'
      : Boolean(initialFilters?.['address.city'])
      ? 'address.city'
      : Boolean(initialFilters?.['address.state'])
      ? 'address.state'
      : 'address.neighbor';

    return {
      searchStrKey,
      searchStrValue: initialFilters?.[searchStrKey],
      propertyType: initialFilters?.['info.type'],
      estrato: initialFilters?.['info.estrato'],
      minPrice: initialFilters?.['prices.property']?.$gte,
      maxPrice: initialFilters?.['prices.property']?.$lte,
      minSquareMts: initialFilters?.['info.squareMeters']?.$gte,
      maxSquareMts: initialFilters?.['info.squareMeters']?.$lte,
      minConstructionYear: initialFilters?.['info.constructionYear']?.$gte,
      maxConstructionYear: initialFilters?.['info.constructionYear']?.$lte,
      rooms: parseOrFilters('info.rooms', initialFilters?.$and),
      toilets: parseOrFilters('info.toilets', initialFilters?.$and),
      parkings: parseOrFilters('info.parkings', initialFilters?.$and),
      features: initialFilters?.features?.$all.map(
        (el: any) => el.$elemMatch.name
      ),
    };
  }, [props.initialFilterStr]);

  const { handleSubmit, register, setValue } = useForm({
    defaultValues,
  });

  const [typesProperty, setTypesProperty] = useState<any>([]);
  const [openNotification, setOpenNotification] = useState(false);

  useEffect(() => {
    getTypeProperties().then((response) => {
      setTypesProperty(response.data.data);
    });
  }, []);

  const { data: optionsData } = useOptionsData();

  const initialFilters =
    props.initialFilterStr && JSON.parse(props.initialFilterStr);
  //states
  const [minCurrency, setMinCurrency] = React.useState<String>(
    initialFilters?.['prices.property']?.$gte
  );
  const [maxCurrency, setMaxCurrency] = React.useState<String>(
    initialFilters?.['prices.property']?.$lte
  );

  const [minSquareMts, setMinSquareMts] = React.useState<Number>(
    initialFilters?.['info.squareMeters']?.$gte
  );
  const [maxSquareMts, setMaxSquareMts] = React.useState<Number>(
    initialFilters?.['info.squareMeters']?.$lte
  );

  const [minAge, setMinAge] = React.useState<Number>(
    initialFilters?.['info.constructionYear']?.$lte
  );
  const [maxAge, setMaxAge] = React.useState<Number>(
    initialFilters?.['info.constructionYear']?.$gte
  );

  const [types, setTypes] = useState<any>(
    initialFilters?.['info.type'] ? initialFilters?.['info.type'] : []
  );
  //Change currency
  const handleChangeMin = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMinCurrency(event.target.value);
  };
  const handleChangeMax = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMaxCurrency(event.target.value);
  };

  useEffect(() => {
    register('searchStrKey');
    register('searchStrValue');
  }, []);

  const handleFormSubmit = handleSubmit((data: any) => {
    localStorage.setItem('data', JSON.stringify(data));
    let filterJson: any = {};

    filterJson = {
      'address.city': undefined,
      'address.state': undefined,
      'address.country': undefined,
      'address.name': undefined,
      ...(data.searchStrValue && { [data.searchStrKey]: data.searchStrValue }),

      'info.type': types.length > 0 ? types : undefined,
      'info.estrato': data.estrato.length > 0 ? data.estrato : undefined,
      $and: [
        { $or: getOrFilters('info.rooms', data.rooms) },
        { $or: getOrFilters('info.toilets', data.toilets) },
        { $or: getOrFilters('info.parkings', data.parkings) },
      ],
      ...setMinMaxFilter('prices.property', data.minPrice, data.maxPrice),
      ...setMinMaxFilter(
        'info.squareMeters',
        data.minSquareMts,
        data.maxSquareMts
      ),
      ...setMinMaxFilter(
        'info.constructionYear',
        data.minConstructionYear,
        data.maxConstructionYear
      ),

      features:
        data.features.length > 0
          ? {
              $all: data.features.map((el: string) => ({
                $elemMatch: { name: el },
              })),
            }
          : undefined,
    };

    filterJson.$and = filterJson.$and.filter(
      (el: any) => Boolean(el.$or) && el.$or.length
    );
    filterJson.$and = filterJson.$and.length > 0 ? filterJson.$and : undefined;
    if (!Object.values(filterJson).every((el) => el === undefined)) {
      props.onSubmit(filterJson);
    }
  });

  //State to save filter
  const [saveFilter, setSaveFilter] = useState<any>('');
  const HandleSaveFilter = handleSubmit((data: any) => {
    let filterJson: any = {};

    filterJson = {
      'address.city': undefined,
      'address.state': undefined,
      'address.country': undefined,
      'address.name': undefined,
      ...(data.searchStrValue && { [data.searchStrKey]: data.searchStrValue }),

      'info.type': types.length > 0 ? types : undefined,
      'info.estrato': data.estrato.length > 0 ? data.estrato : undefined,
      $and: [
        { $or: getOrFilters('info.rooms', data.rooms) },
        { $or: getOrFilters('info.toilets', data.toilets) },
        { $or: getOrFilters('info.parkings', data.parkings) },
      ],
      ...setMinMaxFilter('prices.property', data.minPrice, data.maxPrice),
      ...setMinMaxFilter(
        'info.squareMeters',
        data.minSquareMts,
        data.maxSquareMts
      ),
      ...setMinMaxFilter(
        'info.constructionYear',
        data.minConstructionYear,
        data.maxConstructionYear
      ),

      features:
        data.features.length > 0
          ? {
              $all: data.features.map((el: string) => ({
                $elemMatch: { name: el },
              })),
            }
          : undefined,
    };

    filterJson.$and = filterJson.$and.filter(
      (el: any) => Boolean(el.$or) && el.$or.length
    );
    filterJson.$and = filterJson.$and.length > 0 ? filterJson.$and : undefined;
    if (!Object.values(filterJson).every((el) => el === undefined)) {
      setSaveFilter(JSON.stringify(filterJson));
      setOpenNotification(true);
    }
  });

  //Filter type
  const handleChange = (event: any) => {
    const {
      target: { value },
    } = event;
    let valueString = value;
    setTypes(
      // On autofill we get a stringified value.
      typeof valueString === 'string' ? valueString.split(',') : valueString
    );
  };

  return (
    <>
      <Modal
        open={props.open}
        title="Filtros"
        closeBtn
        onClose={props.onClose}
        footer={
          <div className={styles.footerBtns}>
            <Button
              iconUrl={AlertsIcon}
              label="NOTIFICACIONES"
              onClick={HandleSaveFilter}
              className={styles.colorButton}
            />
            <Button label="ACEPTAR" onClick={handleFormSubmit} />
          </div>
        }
      >
        <form onSubmit={handleFormSubmit} className={styles.content}>
          <section>
            <h2>Busca por ciudad, zona o barrio</h2>
            <AutoComplete
              apiFn={getPlacesAutocomplete}
              filter={true}
              resultsPath="data.data"
              defaultValue={defaultValues.searchStrValue}
              resultItemLabelPath="value"
              onInputChange={(value) => {
                const {
                  searchStrKey,
                  searchStrValue,
                } = getSearchObjectFromAutocomplete(value);
                setValue('searchStrKey', searchStrKey);
                setValue('searchStrValue', searchStrValue);
              }}
              onItemClick={(value) => {
                const {
                  searchStrKey,
                  searchStrValue,
                } = getSearchObjectFromAutocomplete(value);
                setValue('searchStrKey', searchStrKey);
                setValue('searchStrValue', searchStrValue);
              }}
              inputProps={{
                placeholder: 'Busca por ciudad, zona o barrio',
              }}
            />
          </section>

          <section>
            <h2>Tipo de propiedad</h2>
            <Box className={styles.box}>
              <FormControl className={styles.formControl}>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  value={types}
                  onChange={handleChange}
                  input={<OutlinedInput />}
                  renderValue={(selected: any) => selected.join(', ')}
                  MenuProps={MenuProps}
                >
                  {typesProperty.map((type: any) => (
                    <MenuItem key={type._id} value={type._id}>
                      <Checkbox checked={types?.indexOf(type._id) > -1} />
                      <ListItemText
                        primary={type._id + ' (' + type.count + ')'}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </section>

          <section>
            <h2>Precio</h2>
            <TextField
              className={styles.inputMui}
              value={minCurrency}
              {...register('minPrice')}
              onInput={(value: any) => {
                setValue(
                  'minPrice',
                  Number(value.target.value.replace(/[^0-9.-]+/g, ''))
                );
              }}
              placeholder="Mínimo"
              name="minPrice"
              onChange={handleChangeMin}
              InputProps={{
                inputComponent: NumberFormatCustom as any,
                disableUnderline: true,
              }}
              variant="standard"
            />
            <TextField
              className={styles.inputMui}
              value={maxCurrency}
              {...register('maxPrice')}
              onInput={(value: any) => {
                setValue(
                  'maxPrice',
                  Number(value.target.value.replace(/[^0-9.-]+/g, ''))
                );
              }}
              placeholder="Máximo"
              name="maxPrice"
              onChange={handleChangeMax}
              InputProps={{
                inputComponent: NumberFormatCustom as any,
                disableUnderline: true,
              }}
              variant="standard"
            />
          </section>

          <section>
            <h2>Estrato</h2>
            <div className={styles.checkboxBtns}>
              {[1, 2, 3, 4, 5, 6].map((estrato) => (
                <label key={estrato}>
                  <input
                    type="checkbox"
                    name="estrato"
                    value={estrato}
                    ref={register}
                  />
                  <span>{estrato}</span>
                </label>
              ))}
            </div>
          </section>

          <section>
            <h2>Area Metros cuadrados</h2>
            <TextField
              className={styles.inputMui}
              {...register('minSquareMts')}
              value={minSquareMts}
              onInput={(value: any) => {
                setValue(
                  'minSquareMts',
                  Number(value.target.value.replace(/[^0-9.-]+/g, ''))
                );
                setMinSquareMts(
                  Number(value.target.value.replace(/[^0-9.-]+/g, ''))
                );
              }}
              placeholder="Mínimo"
              name="minSquareMts"
              InputProps={{
                disableUnderline: true,
              }}
              variant="standard"
            />
            <TextField
              className={styles.inputMui}
              {...register('maxSquareMts')}
              value={maxSquareMts}
              onInput={(value: any) => {
                setValue(
                  'maxSquareMts',
                  Number(value.target.value.replace(/[^0-9.-]+/g, ''))
                );
                setMaxSquareMts(
                  Number(value.target.value.replace(/[^0-9.-]+/g, ''))
                );
              }}
              placeholder="Máximo"
              name="maxSquareMts"
              InputProps={{
                disableUnderline: true,
              }}
              variant="standard"
            />
          </section>

          <section>
            <h2>Años de construido</h2>
            <TextField
              className={styles.inputMui}
              {...register('minConstructionYear')}
              value={minAge}
              onInput={(value: any) => {
                setValue(
                  'minConstructionYear',
                  Number(value.target.value.replace(/[^0-9.-]+/g, ''))
                );
                setMinAge(Number(value.target.value.replace(/[^0-9.-]+/g, '')));
              }}
              placeholder="Mínimo (ej. 2010)"
              name="minConstructionYear"
              InputProps={{
                disableUnderline: true,
              }}
              variant="standard"
            />
            <TextField
              className={styles.inputMui}
              {...register('maxConstructionYear')}
              value={maxAge}
              onInput={(value: any) => {
                setValue(
                  'maxConstructionYear',
                  Number(value.target.value.replace(/[^0-9.-]+/g, ''))
                );
                setMaxAge(Number(value.target.value.replace(/[^0-9.-]+/g, '')));
              }}
              placeholder="Máximo (ej. 2022)"
              name="maxConstructionYear"
              InputProps={{
                disableUnderline: true,
              }}
              variant="standard"
            />
          </section>

          <section>
            <h2>Habitaciones</h2>
            <div className={styles.checkboxBtns}>
              {[1, 2, 3, '4+'].map((el) => (
                <label key={el}>
                  <input
                    type="checkbox"
                    name="rooms"
                    value={el}
                    ref={register}
                  />
                  <span>{el}</span>
                </label>
              ))}
            </div>
          </section>

          <section>
            <h2>Baños</h2>
            <div className={styles.checkboxBtns}>
              {[1, 2, 3, '4+'].map((el) => (
                <label key={el}>
                  <input
                    type="checkbox"
                    name="toilets"
                    value={el}
                    ref={register}
                  />
                  <span>{el}</span>
                </label>
              ))}
            </div>
          </section>

          <section>
            <h2>Parqueaderos</h2>
            <div className={styles.checkboxBtns}>
              {[1, 2, 3, '4+'].map((el) => (
                <label key={el}>
                  <input
                    type="checkbox"
                    name="parkings"
                    value={el}
                    ref={register}
                  />
                  <span>{el}</span>
                </label>
              ))}
            </div>
          </section>

          <section>
            <h2>Características</h2>
            <div className={styles.checkboxList}>
              {optionsData?.features.map(({ name }: any) => (
                <label key={name}>
                  <input
                    type="checkbox"
                    name="features"
                    value={name}
                    ref={register}
                  />
                  <span>{name}</span>
                </label>
              ))}
            </div>
          </section>

          <input
            type="submit"
            value=""
            style={{ opacity: 0, visibility: 'hidden', position: 'absolute' }}
          />

          <Button
            className={styles.buttonFullwidth}
            label="BORRAR FILTROS"
            onClick={props.onCleanFilters}
          />
        </form>
      </Modal>
      <SaveFilterDialog
        filter={saveFilter}
        open={openNotification}
        onClose={() => setOpenNotification(false)}
      />
    </>
  );
}
